import { BaseSearchRequest } from '../../../models/overmind/search-request';
import { ReducerAction } from '../../../models/reducer-state';
import { EmployeeSearchResponse, PersonListItem } from '../../../models/view/employee-search-response';
import { PeopleCMSLayoutData, PeopleHeaderContentCMSLayoutData } from './freemium-people.cms.models';
import { ColumnConfiguration } from '../../../models/column-configuration';
import { PagePath } from '../../../navigation/navigation.enums';
import { SortField } from '../../../enums';
import { FocusPanelViewData } from '../../../models/focus-panel.models';
import { Breadcrumb } from '@keplerco/core';

interface PeopleCMSState {
  pageTitle: string;
  crumbs: Breadcrumb[];
  request: BaseSearchRequest;
  data: EmployeeSearchResponse | undefined;

  // dialogs
  pendingInvites?: PersonListItem[];
  personToArchive?: PersonListItem;
  personToManage?: FocusPanelViewData<string | undefined>;
  personToDelete?: PersonListItem;
  showBulkUpload?: FocusPanelViewData<undefined>;

  // columns
  managingColumns?: boolean;
  columnConfiguration: ColumnConfiguration[];
}

export const initialState: PeopleCMSState = {
  pageTitle: 'People',
  crumbs: [],
  request: { sortAscending: true, sortField: SortField.Name, pageSize: 15 },
  data: undefined,

  // columns
  columnConfiguration: [
    { label: 'Name', key: 'Name', required: true },
    { label: 'Email', key: 'Email', required: false, selected: true },
    { label: 'Job title', key: 'Jobtitle', required: false, selected: true },
    { label: 'Status', key: 'Status', required: false, selected: true },
    { label: 'Date Created', key: 'DateCreated', required: false, selected: true },
  ]
};

export enum PeopleCMSActionTypes {
  SetHeader = 0,
  SetRequest = 1,
  SetData = 2,

  // dialogs
  SetBulkUpload = 3,
  SetPendingInvites = 4,
  SetPersonToArchive = 5,
  SetPersonToManage = 6,
  SetShowBulkUpload = 14,

  // columns
  ManageColumns = 7,
  EditColumnConfiguration = 8,

  // filters
  SetDepartments = 9,
  SetTeams = 10,
  SetCountries = 11,
  SetRoles = 12,
  SetPersonToDelete = 13,
}

type PeopleCMSActions =
  | ReducerAction<PeopleCMSActionTypes.SetHeader, { payload: PeopleHeaderContentCMSLayoutData }>
  | ReducerAction<PeopleCMSActionTypes.SetRequest, { payload: BaseSearchRequest }>
  | ReducerAction<PeopleCMSActionTypes.SetData, { payload: PeopleCMSLayoutData }>

  // dialogs
  | ReducerAction<PeopleCMSActionTypes.SetPendingInvites, { payload: PersonListItem[] | undefined }>
  | ReducerAction<PeopleCMSActionTypes.SetPersonToArchive, { payload: PersonListItem | undefined }>
  | ReducerAction<PeopleCMSActionTypes.SetPersonToManage, { payload: FocusPanelViewData<string | undefined> }>
  | ReducerAction<PeopleCMSActionTypes.SetPersonToDelete, { payload: PersonListItem | undefined }>
  | ReducerAction<PeopleCMSActionTypes.SetShowBulkUpload, { payload: FocusPanelViewData<undefined> }>

  // columns
  | ReducerAction<PeopleCMSActionTypes.EditColumnConfiguration, { payload: ColumnConfiguration[] }>
  | ReducerAction<PeopleCMSActionTypes.ManageColumns, { payload: { open: boolean; reset?: boolean } }>;

export function reducer(state: PeopleCMSState, action: PeopleCMSActions): PeopleCMSState {
  if (action.type === PeopleCMSActionTypes.SetHeader) {
    const crumbs: Breadcrumb[] = [
      { name: 'User Management', url: PagePath.freemiumUserManagementBase },
      { name: 'People', url: window.location.pathname },
    ];
    return {
      ...state,
      pageTitle: 'People',
      crumbs: crumbs,
    };
  }

  if (action.type === PeopleCMSActionTypes.SetRequest) {
    return { ...state, request: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetData) {
    return {
      ...state,
      data: action.payload.data,
    };
  }

  // dialogs

  if (action.type === PeopleCMSActionTypes.SetPendingInvites) {
    return { ...state, pendingInvites: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetPersonToArchive) {
    return { ...state, personToArchive: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetPersonToManage) {
    return { ...state, personToManage: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetPersonToDelete) {
    return { ...state, personToDelete: action.payload };
  }

  if (action.type === PeopleCMSActionTypes.SetShowBulkUpload) {
    return { ...state, showBulkUpload: action.payload };
  }

  // columns
  if (action.type === PeopleCMSActionTypes.ManageColumns) {
    if (!action.payload.reset) return { ...state, managingColumns: action.payload.open };
    else if (action.payload.reset) return { ...state, managingColumns: action.payload.open, columnConfiguration: [...initialState.columnConfiguration] };
  }

  if (action.type === PeopleCMSActionTypes.EditColumnConfiguration) {
    return { ...state, columnConfiguration: action.payload };
  }
  return state;
}

